import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import type {
  GuardianEntry,
  GuardianshipEntry,
  MemberGender,
  MemberResponse,
  MemberRoleKey,
  MemberTrainingGroupEntry,
  TeamAssignmentEntry,
  TeamResponse,
  TrainingGroupResponse,
} from '@bm/contracts';
import { MEMBER_GENDER_LABELS, MEMBER_ROLE_LABELS } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field, Input } from '../components/ui';

export function MemberDetailPage() {
  const { id } = useParams({ strict: false });
  const qc = useQueryClient();
  const { can } = useAuth();

  const { data: member } = useQuery({
    queryKey: ['member', id],
    queryFn: () => api<MemberResponse>(`/members/${id}`, { orgScoped: true }),
  });
  const { data: teams } = useQuery({
    queryKey: ['member', id, 'teams'],
    queryFn: () => api<TeamAssignmentEntry[]>(`/members/${id}/teams`, { orgScoped: true }),
  });
  const { data: allTeams } = useQuery({
    queryKey: ['teams', 'all'],
    queryFn: () => api<{ data: TeamResponse[] }>('/teams?pageSize=200', { orgScoped: true }),
    enabled: can('team_members.manage') || can('members.manage'),
  });
  const { data: teamManagerTeamIds } = useQuery({
    queryKey: ['member', id, 'team-manager-teams'],
    queryFn: () => api<string[]>(`/members/${id}/team-manager-teams`, { orgScoped: true }),
    enabled: can('members.view'),
  });
  const { data: memberGroups } = useQuery({
    queryKey: ['member', id, 'training-groups'],
    queryFn: () => api<MemberTrainingGroupEntry[]>(`/training/members/${id}/groups`, { orgScoped: true }),
    enabled: can('training.view'),
  });
  const { data: allGroups } = useQuery({
    queryKey: ['training', 'groups'],
    queryFn: () => api<TrainingGroupResponse[]>('/training/groups', { orgScoped: true }),
    enabled: can('training.view'),
  });
  const { data: guardians } = useQuery({
    queryKey: ['member', id, 'guardians'],
    queryFn: () => api<GuardianEntry[]>(`/members/${id}/guardians`, { orgScoped: true }),
    enabled: can('members.view'),
  });
  const { data: guardianships } = useQuery({
    queryKey: ['member', id, 'guardianships'],
    queryFn: () => api<GuardianshipEntry[]>(`/members/${id}/guardianships`, { orgScoped: true }),
    enabled: can('members.view'),
  });

  // Stammdaten (Name, Geschlecht, Geburtstag, Notfall-Telefon) — bewusst
  // getrennt von "Kontozugang" (App-Rolle/E-Mail/Passwort) unten, sowohl im
  // Formular-State als auch im PATCH-Body: das sind genau die Felder, die ein
  // Mitglied auch selbst über "Konto bearbeiten" (ProfileMenu.tsx,
  // `PATCH /members/me`) ändern darf, siehe docs/members.md.
  const [stammForm, setStammForm] = useState({
    firstName: '',
    lastName: '',
    gender: '' as MemberGender | '',
    birthDate: '',
    emergencyPhone: '',
  });
  const [stammSaved, setStammSaved] = useState(false);
  const [stammError, setStammError] = useState<string | null>(null);

  // Kontozugang (App-Rolle, E-Mail/Passwort für den Login) — nie über
  // "Konto bearbeiten" durch das Mitglied selbst änderbar, nur hier von
  // einem Vereinsadmin.
  const [accessForm, setAccessForm] = useState({
    email: '',
    password: '',
    roleKey: 'member' as MemberRoleKey,
  });
  const [accessSaved, setAccessSaved] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);

  useEffect(() => {
    if (member) {
      setStammForm({
        firstName: member.firstName,
        lastName: member.lastName,
        gender: member.gender ?? '',
        birthDate: member.birthDate ?? '',
        emergencyPhone: member.emergencyPhone ?? '',
      });
      setAccessForm({
        email: member.email ?? '',
        password: '',
        roleKey: member.roleKey ?? 'member',
      });
    }
  }, [member]);

  const saveStamm = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<MemberResponse>(`/members/${id}`, { method: 'PATCH', orgScoped: true, body }),
    onSuccess: () => {
      setStammError(null);
      setStammSaved(true);
      void qc.invalidateQueries({ queryKey: ['member', id] });
      setTimeout(() => setStammSaved(false), 2000);
    },
    onError: (e) => setStammError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  const saveAccess = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<MemberResponse>(`/members/${id}`, { method: 'PATCH', orgScoped: true, body }),
    onSuccess: () => {
      setAccessError(null);
      setAccessSaved(true);
      setAccessForm((f) => ({ ...f, password: '' }));
      void qc.invalidateQueries({ queryKey: ['member', id] });
      setTimeout(() => setAccessSaved(false), 2000);
    },
    onError: (e) => setAccessError(e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.'),
  });

  const toggleTeam = useMutation({
    mutationFn: ({ teamId, on }: { teamId: string; on: boolean }) => {
      const existing = teams?.find((t) => t.teamId === teamId);
      return on
        ? api(`/teams/${teamId}/members`, { method: 'POST', orgScoped: true, body: { memberId: id } })
        : api(`/teams/${teamId}/members/${existing?.id}`, { method: 'DELETE', orgScoped: true });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['member', id, 'teams'] }),
  });

  const toggleGroup = useMutation({
    mutationFn: ({ groupId, on }: { groupId: string; on: boolean }) =>
      on
        ? api(`/training/groups/${groupId}/members`, { method: 'POST', orgScoped: true, body: { memberId: id } })
        : api(`/training/groups/${groupId}/members/${id}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['member', id, 'training-groups'] }),
  });

  // Vereinsrollen (additiv zur App-Rolle, siehe docs/permissions.md):
  // Mannschaftsführer für ausgewählte Mannschaften + Social-Media-Beauftragte.
  // "Mannschaftsführer" ist als Checkbox nur die UI-Klappe für die
  // Mehrfachauswahl darunter — lokal offen gehalten (nicht rein aus der
  // Anzahl zugewiesener Mannschaften abgeleitet), sonst ließe sich die
  // Mehrfachauswahl nie öffnen, um die erste Mannschaft überhaupt
  // auszuwählen.
  const [teamManagerExpanded, setTeamManagerExpanded] = useState(false);
  useEffect(() => {
    if (teamManagerTeamIds) setTeamManagerExpanded(teamManagerTeamIds.length > 0);
  }, [teamManagerTeamIds]);

  const toggleTeamManagerTeam = useMutation({
    mutationFn: ({ teamId, on, clearAll }: { teamId: string; on: boolean; clearAll?: boolean }) => {
      const current = teamManagerTeamIds ?? [];
      const next = clearAll ? [] : on ? [...current, teamId] : current.filter((t) => t !== teamId);
      return api(`/members/${id}/team-manager-teams`, { method: 'PUT', orgScoped: true, body: { teamIds: next } });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['member', id, 'team-manager-teams'] }),
  });
  const toggleSelfRegistration = useMutation({
    mutationFn: (on: boolean) =>
      api<MemberResponse>(`/members/${id}`, { method: 'PATCH', orgScoped: true, body: { selfRegistrationEnabled: on } }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['member', id] }),
  });

  const [guardianEmail, setGuardianEmail] = useState('');
  const addGuardian = useMutation({
    mutationFn: () =>
      api(`/members/${id}/guardians`, { method: 'POST', orgScoped: true, body: { email: guardianEmail, canManage: true } }),
    onSuccess: () => {
      setGuardianEmail('');
      void qc.invalidateQueries({ queryKey: ['member', id, 'guardians'] });
    },
  });
  const removeGuardian = useMutation({
    mutationFn: (guardianId: string) => api(`/members/${id}/guardians/${guardianId}`, { method: 'DELETE', orgScoped: true }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['member', id, 'guardians'] }),
  });

  if (!member) return <p className="text-sm text-slate-500">Lädt…</p>;
  const readOnly = !can('members.edit');

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">
        {member.firstName} {member.lastName}
      </h1>

      <Card title="Stammdaten">
        {stammSaved && <Alert kind="success">Gespeichert.</Alert>}
        {stammError && <Alert>{stammError}</Alert>}
        <form
          className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            saveStamm.mutate({
              firstName: stammForm.firstName,
              lastName: stammForm.lastName,
              gender: stammForm.gender || null,
              birthDate: stammForm.birthDate || null,
              emergencyPhone: stammForm.emergencyPhone || null,
            });
          }}
        >
          <Field label="Vorname">
            <Input
              value={stammForm.firstName}
              disabled={readOnly}
              required
              onChange={(e) => setStammForm((f) => ({ ...f, firstName: e.target.value }))}
            />
          </Field>
          <Field label="Nachname">
            <Input
              value={stammForm.lastName}
              disabled={readOnly}
              required
              onChange={(e) => setStammForm((f) => ({ ...f, lastName: e.target.value }))}
            />
          </Field>
          <Field label="Geschlecht (für die Mannschaftsaufstellung)">
            <select
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent disabled:bg-slate-50"
              value={stammForm.gender}
              disabled={readOnly}
              onChange={(e) => setStammForm((f) => ({ ...f, gender: e.target.value as MemberGender | '' }))}
            >
              <option value="">Nicht angegeben</option>
              {Object.entries(MEMBER_GENDER_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Geburtstag (optional)">
            <Input
              type="date"
              value={stammForm.birthDate}
              disabled={readOnly}
              onChange={(e) => setStammForm((f) => ({ ...f, birthDate: e.target.value }))}
            />
          </Field>
          <Field label="Telefonnummer für Notfälle (optional)">
            <Input
              type="tel"
              value={stammForm.emergencyPhone}
              disabled={readOnly}
              onChange={(e) => setStammForm((f) => ({ ...f, emergencyPhone: e.target.value }))}
            />
          </Field>
          {!readOnly && (
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saveStamm.isPending}>
                Speichern
              </Button>
            </div>
          )}
        </form>
      </Card>

      <Card title="Kontozugang">
        {accessSaved && <Alert kind="success">Gespeichert.</Alert>}
        {accessError && <Alert>{accessError}</Alert>}
        <form
          className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            const body: Record<string, unknown> = {
              email: accessForm.email,
              roleKey: accessForm.roleKey,
            };
            if (accessForm.password) body.password = accessForm.password;
            saveAccess.mutate(body);
          }}
        >
          <Field label="E-Mail (für Login)">
            <Input
              type="email"
              value={accessForm.email}
              disabled={readOnly}
              required
              onChange={(e) => setAccessForm((f) => ({ ...f, email: e.target.value }))}
            />
          </Field>
          <Field label="App-Rolle">
            <select
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent disabled:bg-slate-50"
              value={accessForm.roleKey}
              disabled={readOnly}
              onChange={(e) => setAccessForm((f) => ({ ...f, roleKey: e.target.value as MemberRoleKey }))}
            >
              {Object.entries(MEMBER_ROLE_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Neues Passwort (leer lassen zum Beibehalten)">
            <Input
              type="text"
              value={accessForm.password}
              disabled={readOnly}
              minLength={10}
              placeholder="mind. 10 Zeichen"
              onChange={(e) => setAccessForm((f) => ({ ...f, password: e.target.value }))}
            />
          </Field>
          {!readOnly && (
            <div className="sm:col-span-2">
              <Button type="submit" disabled={saveAccess.isPending}>
                Speichern
              </Button>
            </div>
          )}
        </form>
      </Card>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {can('team_members.manage') || (teams && teams.length > 0) ? (
          <Card title="Mannschaftszugehörigkeit">
            {allTeams && can('team_members.manage') ? (
              allTeams.data.length > 0 ? (
                <div className="flex flex-wrap gap-3">
                  {allTeams.data.map((t) => {
                    const checked = teams?.some((a) => a.teamId === t.id) ?? false;
                    return (
                      <label key={t.id} className="flex items-center gap-1.5 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={toggleTeam.isPending}
                          onChange={(e) => toggleTeam.mutate({ teamId: t.id, on: e.target.checked })}
                        />
                        {t.name}
                      </label>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-slate-500">Noch keine Mannschaften angelegt.</p>
              )
            ) : teams && teams.length > 0 ? (
              <p className="text-sm text-slate-600">{teams.map((t) => t.teamName).join(', ')}</p>
            ) : (
              <p className="text-sm text-slate-500">Keine Zuordnung.</p>
            )}
            {toggleTeam.isError && (
              <Alert>{toggleTeam.error instanceof Error ? toggleTeam.error.message : 'Zuordnung fehlgeschlagen.'}</Alert>
            )}
          </Card>
        ) : null}

        {can('training.view') && (
          <Card title="Trainingsgruppe">
            {allGroups && allGroups.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {allGroups.map((g) => {
                  const checked = memberGroups?.some((mg) => mg.groupId === g.id) ?? false;
                  return (
                    <label key={g.id} className="flex items-center gap-1.5 text-sm">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!can('training.manage') || toggleGroup.isPending}
                        onChange={(e) => toggleGroup.mutate({ groupId: g.id, on: e.target.checked })}
                      />
                      {g.name}
                    </label>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500">Noch keine Trainingsgruppen angelegt.</p>
            )}
            {toggleGroup.isError && (
              <Alert>{toggleGroup.error instanceof Error ? toggleGroup.error.message : 'Zuordnung fehlgeschlagen.'}</Alert>
            )}
          </Card>
        )}
      </div>

      {(can('members.manage') ||
        can('members.edit') ||
        (teamManagerTeamIds && teamManagerTeamIds.length > 0)) && (
        <Card title="Vereinsrollen">
          <p className="mb-3 text-xs text-slate-500">
            Zusätzlich zur App-Rolle oben — additiv, unabhängig davon, welche App-Rolle sonst gilt.
          </p>
          <div className="space-y-3">
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium">
                <input
                  type="checkbox"
                  checked={teamManagerExpanded}
                  disabled={!can('members.manage') || toggleTeamManagerTeam.isPending}
                  onChange={(e) => {
                    setTeamManagerExpanded(e.target.checked);
                    if (!e.target.checked && teamManagerTeamIds && teamManagerTeamIds.length > 0) {
                      // Abwählen räumt alle zugewiesenen Mannschaften auf einmal auf.
                      toggleTeamManagerTeam.mutate({ teamId: '', on: false, clearAll: true });
                    }
                  }}
                />
                Mannschaftsführer
              </label>
              {teamManagerExpanded && allTeams && (
                <div className="mt-2 flex flex-wrap gap-3 pl-5">
                  {allTeams.data.length === 0 && <p className="text-xs text-slate-500">Noch keine Mannschaften angelegt.</p>}
                  {allTeams.data.map((t) => {
                    const checked = teamManagerTeamIds?.includes(t.id) ?? false;
                    return (
                      <label key={t.id} className="flex items-center gap-1.5 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!can('members.manage') || toggleTeamManagerTeam.isPending}
                          onChange={(e) => toggleTeamManagerTeam.mutate({ teamId: t.id, on: e.target.checked })}
                        />
                        {t.name}
                      </label>
                    );
                  })}
                </div>
              )}
              {teamManagerExpanded && can('members.manage') && !teamManagerTeamIds?.length && (
                <p className="mt-1 pl-5 text-xs text-slate-500">
                  Mannschaften auswählen — für diese darf {member.firstName} anschließend auf der
                  Saisonspiele-Seite die Spiele bearbeiten.
                </p>
              )}
              {toggleTeamManagerTeam.isError && (
                <Alert>
                  {toggleTeamManagerTeam.error instanceof ApiError
                    ? toggleTeamManagerTeam.error.message
                    : 'Zuordnung fehlgeschlagen.'}
                </Alert>
              )}
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card title="Elternkonten / Erziehungsberechtigte">
          {guardians && guardians.length > 0 ? (
            <ul className="space-y-1 text-sm">
              {guardians.map((g) => (
                <li key={g.id} className="flex items-center justify-between gap-2">
                  <span>
                    {g.guardianName ?? g.guardianEmail ?? g.guardianUserId}
                    {g.guardianName && g.guardianEmail ? ` (${g.guardianEmail})` : ''}
                    {g.relation ? ` · ${g.relation}` : ''}
                    {g.canManage ? ' · darf verwalten' : ''}
                  </span>
                  {can('members.manage') && (
                    <Button
                      variant="ghost"
                      className="px-2 py-1 text-xs"
                      onClick={() => removeGuardian.mutate(g.id)}
                    >
                      Entfernen
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-slate-500">Keine verknüpft.</p>
          )}
          {can('members.manage') && (
            <form
              className="mt-3 flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                addGuardian.mutate();
              }}
            >
              <Input
                placeholder="E-Mail des Elternteils"
                value={guardianEmail}
                onChange={(e) => setGuardianEmail(e.target.value)}
              />
              <Button type="submit" variant="ghost">
                Hinzufügen
              </Button>
            </form>
          )}
          {addGuardian.isError && (
            <Alert>
              {addGuardian.error instanceof ApiError ? addGuardian.error.message : 'Hinzufügen fehlgeschlagen.'}
            </Alert>
          )}
        </Card>

        {guardianships && guardianships.length > 0 && (
          <Card title="Verknüpfte Kinder">
            <p className="mb-2 text-xs text-slate-500">
              Kinder, für die {member.firstName} als Erziehungsberechtigte(r) handeln darf. Jedes Profil hat seine
              eigene Trainingsgruppen-/Mannschaftszuordnung — nur dafür bekommt es Rückmelde-Optionen angezeigt,
              nie für die Gruppen der anderen verknüpften Profile.
            </p>
            {(memberGroups?.length ?? 0) > 0 || (teams?.length ?? 0) > 0 ? (
              <p className="mb-2 text-sm">
                {member.firstName} selbst:{' '}
                <span className="inline-flex flex-wrap gap-1 align-middle">
                  {memberGroups?.map((g) => (
                    <span
                      key={g.groupId}
                      className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500"
                    >
                      {g.groupName}
                    </span>
                  ))}
                  {teams?.map((t) => (
                    <span
                      key={t.teamId}
                      className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500"
                    >
                      {t.teamName}
                    </span>
                  ))}
                </span>
              </p>
            ) : null}
            <ul className="space-y-1.5 text-sm">
              {guardianships.map((g) => (
                <li key={g.minorMemberId}>
                  {g.minorName}
                  {g.relation ? ` · ${g.relation}` : ''}
                  {g.canManage ? ' · darf verwalten' : ''}
                  {(g.trainingGroupNames.length > 0 || g.teamNames.length > 0) && (
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      {g.trainingGroupNames.map((name) => (
                        <span
                          key={name}
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500"
                        >
                          {name}
                        </span>
                      ))}
                      {g.teamNames.map((name) => (
                        <span
                          key={name}
                          className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500"
                        >
                          {name}
                        </span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <label className="mt-3 flex items-center gap-1.5 border-t border-slate-100 pt-3 text-sm font-medium">
              <input
                type="checkbox"
                checked={member.selfRegistrationEnabled}
                disabled={!can('members.manage') || toggleSelfRegistration.isPending}
                onChange={(e) => toggleSelfRegistration.mutate(e.target.checked)}
              />
              Kann sich auch selbst anmelden
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Standardmäßig aus — {member.firstName} erscheint bei Saisonspiel-/Trainings-Abstimmungen dann nur als
              Option für die Kinder oben, nicht für sich selbst. Kann auch vom Mitglied selbst im Profilmenü
              umgeschaltet werden.
            </p>
          </Card>
        )}
      </div>
    </div>
  );
}
